import { useState, useEffect } from 'react'
import api from '../../utils/api'
import AdminLayout from '../../components/admin/AdminLayout'
import './Admin.css'

interface GradeData {
  enrollmentId: number
  gradeId?: number
  score: string
  grade: string
  isEditing: boolean
  originalScore?: string
  originalGrade?: string
}

export default function AdminGrades() {
  const [courses, setCourses] = useState<any[]>([])
  const [allCourses, setAllCourses] = useState<any[]>([])
  const [semesters, setSemesters] = useState<any[]>([])
  const [filterSemester, setFilterSemester] = useState<string>('')
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null)
  const [enrollments, setEnrollments] = useState<any[]>([])
  const [grades, setGrades] = useState<{ [key: number]: GradeData }>({})
  const [uploading, setUploading] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  useEffect(() => {
    loadSemesters()
    loadCourses()
  }, [])

  useEffect(() => {
    if (selectedCourse) {
      loadEnrollments()
    } else {
      setEnrollments([])
      setGrades({})
    }
  }, [selectedCourse])

  useEffect(() => {
    filterCourses()
    setSelectedCourse(null)
  }, [filterSemester, allCourses])

  const loadSemesters = async () => {
    try {
      const res = await api.get('/semesters')
      setSemesters(res.data)
    } catch (err) {
      console.error('학기 로드 오류:', err)
    }
  }

  const loadCourses = async () => {
    try {
      const res = await api.get('/admin/courses')
      setAllCourses(res.data)
    } catch (err) {
      console.error(err)
    }
  }

  const filterCourses = () => {
    if (!filterSemester) {
      setCourses(allCourses)
    } else {
      const filtered = allCourses.filter(c => c.semester_id === parseInt(filterSemester))
      setCourses(filtered)
    }
  }

  const loadEnrollments = async () => {
    try {
      const res = await api.get(`/admin/courses/${selectedCourse}/enrollments`)
      const enrollmentsData = res.data
      setEnrollments(enrollmentsData)
      
      // 성적 데이터 초기화
      const gradesData: { [key: number]: GradeData } = {}
      enrollmentsData.forEach((e: any) => {
        gradesData[e.id] = {
          enrollmentId: e.id,
          gradeId: e.grade_id,
          score: e.score || '',
          grade: e.grade || '',
          isEditing: false,
          originalScore: e.score || '',
          originalGrade: e.grade || ''
        }
      })
      setGrades(gradesData)
    } catch (err) {
      console.error(err)
    }
  }

  const handleEdit = (enrollmentId: number) => {
    setGrades(prev => ({
      ...prev,
      [enrollmentId]: {
        ...prev[enrollmentId],
        isEditing: true
      }
    }))
  }

  const handleCancel = (enrollmentId: number) => {
    setGrades(prev => ({
      ...prev,
      [enrollmentId]: {
        ...prev[enrollmentId],
        isEditing: false,
        score: prev[enrollmentId].originalScore || '',
        grade: prev[enrollmentId].originalGrade || ''
      }
    }))
  }

  const handleScoreChange = (enrollmentId: number, value: string) => {
    setGrades(prev => ({
      ...prev,
      [enrollmentId]: {
        ...prev[enrollmentId],
        score: value
      }
    }))
  }

  const handleGradeChange = (enrollmentId: number, value: string) => {
    setGrades(prev => ({
      ...prev,
      [enrollmentId]: {
        ...prev[enrollmentId],
        grade: value
      }
    }))
  }

  const handleSave = async (enrollmentId: number) => {
    const gradeData = grades[enrollmentId]
    if (!gradeData) return

    try {
      if (gradeData.gradeId) {
        // 기존 성적 수정
        await api.put(`/grades/${gradeData.gradeId}`, {
          score: gradeData.score ? parseInt(gradeData.score) : null,
          grade: gradeData.grade || null,
          completionStatus: 'completed'
        })
      } else {
        // 새 성적 입력
        await api.post('/grades', {
          enrollmentId,
          score: gradeData.score ? parseInt(gradeData.score) : null,
          grade: gradeData.grade || null,
          completionStatus: 'completed'
        })
      }
      
      // 성공 후 상태 업데이트
      setGrades(prev => ({
        ...prev,
        [enrollmentId]: {
          ...prev[enrollmentId],
          isEditing: false,
          originalScore: gradeData.score,
          originalGrade: gradeData.grade
        }
      }))
      
      // 데이터 다시 로드
      await loadEnrollments()
    } catch (err: any) {
      alert(err.response?.data?.error || '성적 저장에 실패했습니다.')
    }
  }

  const handleSaveAll = async () => {
    const editingGrades = Object.values(grades).filter(g => g.isEditing)
    if (editingGrades.length === 0) {
      alert('저장할 성적이 없습니다.')
      return
    }

    try {
      const promises = editingGrades.map(gradeData => {
        if (gradeData.gradeId) {
          return api.put(`/grades/${gradeData.gradeId}`, {
            score: gradeData.score ? parseInt(gradeData.score) : null,
            grade: gradeData.grade || null,
            completionStatus: 'completed'
          })
        } else {
          return api.post('/grades', {
            enrollmentId: gradeData.enrollmentId,
            score: gradeData.score ? parseInt(gradeData.score) : null,
            grade: gradeData.grade || null,
            completionStatus: 'completed'
          })
        }
      })

      await Promise.all(promises)
      alert('모든 성적이 저장되었습니다.')
      await loadEnrollments()
    } catch (err: any) {
      alert(err.response?.data?.error || '성적 저장에 실패했습니다.')
    }
  }

  const handleDownloadTemplate = async () => {
    if (!selectedCourse) {
      alert('강의를 먼저 선택해주세요.')
      return
    }

    try {
      const response = await api.get(`/admin/courses/${selectedCourse}/grades/template`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `성적_업로드_템플릿.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      alert(err.response?.data?.error || '템플릿 다운로드에 실패했습니다.')
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0])
    }
  }

  const handleUploadGrades = async () => {
    if (!selectedCourse) {
      alert('강의를 먼저 선택해주세요.')
      return
    }

    if (!uploadFile) {
      alert('업로드할 파일을 선택해주세요.')
      return
    }

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('file', uploadFile)

      const response = await api.post(`/admin/courses/${selectedCourse}/grades/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const result = response.data
      let message = `성적 업로드 완료!\n성공: ${result.success}건`
      if (result.error > 0) {
        message += `\n실패: ${result.error}건`
        if (result.errors && result.errors.length > 0) {
          message += `\n\n오류 내역:\n${result.errors.join('\n')}`
        }
      }
      alert(message)
      
      setUploadFile(null)
      // 파일 input 초기화
      const fileInput = document.getElementById('grade-upload-file') as HTMLInputElement
      if (fileInput) fileInput.value = ''
      
      await loadEnrollments()
    } catch (err: any) {
      alert(err.response?.data?.error || '성적 업로드에 실패했습니다.')
    } finally {
      setUploading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!selectedCourse) {
      alert('강의를 먼저 선택해주세요.')
      return
    }

    try {
      const response = await api.get(`/admin/courses/${selectedCourse}/grades/export`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `성적목록_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      alert(err.response?.data?.error || '엑셀 다운로드에 실패했습니다.')
    }
  }

  return (
    <AdminLayout>
      <div className="admin-page">
        <h1>성적 관리</h1>
        
        <div style={{ 
          display: 'flex', 
          gap: '1.5rem', 
          marginBottom: '1.5rem',
          padding: '1rem',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div className="form-row" style={{ flex: 1, margin: 0 }}>
            <label style={{ fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>학기 필터:</label>
            <select 
              value={filterSemester} 
              onChange={(e) => setFilterSemester(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
            >
              <option value="">전체</option>
              {semesters.map(s => (
                <option key={s.id} value={s.id}>{s.year}년 {s.season}</option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ flex: 2, margin: 0 }}>
            <label style={{ fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>강의 선택:</label>
            <select 
              value={selectedCourse || ''} 
              onChange={(e) => setSelectedCourse(e.target.value ? parseInt(e.target.value) : null)}
              style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
            >
              <option value="">선택하세요</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{c.title} ({c.instructor})</option>
              ))}
            </select>
          </div>
        </div>
        
        {courses.length === 0 && filterSemester && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            background: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffc107',
            marginBottom: '1rem'
          }}>
            <p style={{ color: '#856404', margin: 0 }}>
              ⚠️ 선택한 학기에 등록된 강의가 없습니다.
            </p>
          </div>
        )}
      {selectedCourse && enrollments.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px', 
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '2px dashed #dee2e6'
        }}>
          <p style={{ fontSize: '1.1rem', color: '#666', margin: 0 }}>
            👥 해당 강의에 수강생이 없습니다.
          </p>
        </div>
      )}
      
      {selectedCourse && enrollments.length > 0 && (
        <div className="admin-list">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
            <h2 style={{ margin: 0 }}>수강생 목록 ({enrollments.length}명)</h2>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                onClick={handleExportExcel}
                className="btn btn-primary"
                style={{ padding: '10px 20px', fontSize: '14px' }}
              >
                📊 엑셀 다운로드
              </button>
              <button 
                onClick={handleDownloadTemplate}
                className="btn btn-secondary"
                style={{ padding: '10px 20px', fontSize: '14px' }}
              >
                📥 템플릿 다운로드
              </button>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <input
                  id="grade-upload-file"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="grade-upload-file"
                  className="btn btn-secondary"
                  style={{ 
                    padding: '10px 20px', 
                    fontSize: '14px', 
                    cursor: 'pointer',
                    margin: 0
                  }}
                >
                  📤 파일 선택
                </label>
                {uploadFile && (
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {uploadFile.name}
                  </span>
                )}
                <button 
                  onClick={handleUploadGrades}
                  className="btn btn-primary"
                  style={{ padding: '10px 20px', fontSize: '14px' }}
                  disabled={!uploadFile || uploading}
                >
                  {uploading ? '업로드 중...' : '📋 일괄 업로드'}
                </button>
              </div>
              <button 
                onClick={handleSaveAll}
                className="btn btn-primary"
                style={{ padding: '10px 20px', fontSize: '14px' }}
              >
                💾 전체 저장
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>성명</th>
                  <th>이메일</th>
                  <th>점수</th>
                  <th>등급</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map(e => {
                  const gradeData = grades[e.id]
                  if (!gradeData) return null
                  
                  return (
                    <tr key={e.id}>
                      <td>{e.name}</td>
                      <td>{e.user_email || e.email || '-'}</td>
                      <td>
                        {gradeData.isEditing ? (
                          <input 
                            type="number" 
                            value={gradeData.score}
                            onChange={(evt) => handleScoreChange(e.id, evt.target.value)}
                            style={{ width: '100px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                            placeholder="점수"
                          />
                        ) : (
                          <span style={{ 
                            fontWeight: gradeData.score ? 'bold' : 'normal',
                            color: gradeData.score ? '#333' : '#999'
                          }}>
                            {gradeData.score || '-'}
                          </span>
                        )}
                      </td>
                      <td>
                        {gradeData.isEditing ? (
                          <input 
                            type="text" 
                            value={gradeData.grade}
                            onChange={(evt) => handleGradeChange(e.id, evt.target.value)}
                            style={{ width: '100px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                            placeholder="등급"
                          />
                        ) : (
                          <span style={{ 
                            fontWeight: gradeData.grade ? 'bold' : 'normal',
                            color: gradeData.grade ? '#333' : '#999',
                            fontSize: gradeData.grade ? '14px' : '14px'
                          }}>
                            {gradeData.grade || '-'}
                          </span>
                        )}
                      </td>
                      <td>
                        {gradeData.isEditing ? (
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button 
                              onClick={() => handleSave(e.id)}
                              className="btn btn-primary"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                            >
                              저장
                            </button>
                            <button 
                              onClick={() => handleCancel(e.id)}
                              className="btn btn-secondary"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <button 
                            onClick={() => handleEdit(e.id)}
                            className="btn btn-secondary"
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            수정
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  )
}

